import React, {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import DatePicker from 'react-datepicker';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Select from 'react-select/async';
import debounce from 'debounce-promise';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAsterisk,
  faCalendarDays,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';

import { BrandPrimarySpinningCircle } from '../LoadingCircle';
import { UNLIMITED } from '../../Constants/ActivationCodes';
import {
  ALL_TYPE,
  CALENDAR_TYPE,
  SIGNAL_TYPE,
} from '../../Constants/GtemplateReminder';
import { BILLING } from '../../Constants/RBAC';
import { PROVISION_MODEL_ID_SET } from '../../Constants/StaticUUIDs';
import IconsContext from '../../Context/IconsContext';
import OrgsMapContext from '../../Context/OrgsMapContext';
import { hasWriteAccessToResource } from '../../RBAC/AccessTools';
import AssetService from '../../Services/AssetService';
import AuthService from '../../Services/AuthService';
import GtemplateReminderService from '../../Services/GtemplateReminderService';
import TemplateService from '../../Services/TemplateService';
import { BASIC_SELECT_STYLES } from '../../Styles/SelectStyles';

export default memo(ActivateAssetModal);

const initState = {
  activationModelReminders: [],
  associationExists: false,
  associationCheckPending: true,
  activationCodeSearchPending: false,
  error: '',
  instanceInitValuesMap: new Map(),
  modelSearchPending: true,
  remindersLoading: false,
  requestPending: false,
};

function ActivateAssetModal(props) {
  const { reminderIcon } = useContext(IconsContext),
    orgsMap = useContext(OrgsMapContext);

  const assetOrgBillingEnabled = useMemo(
    () => orgsMap?.[props.asset?.orgID]?.features?.billing?.enabled ?? false,
    [orgsMap, props.asset?.orgID]
  );

  const [state, updateState] = useState(() => ({
    ...initState,
    associationCheckPending: assetOrgBillingEnabled,
  }));

  const assetService = useMemo(() => new AssetService(), []),
    authService = useMemo(() => new AuthService(), []),
    hasBillingWritePermission = useMemo(
      () => hasWriteAccessToResource(props.asset?.orgID, BILLING),
      [props.asset?.orgID]
    ),
    reminderService = useMemo(() => new GtemplateReminderService(), []),
    sessionUserRootOrgID = useMemo(
      () => authService.getUserRootOrgID(),
      [authService]
    ),
    templateService = useMemo(() => new TemplateService(), []);

  let source = CancelToken.source();

  const fetchActivationCodes = async (po_number = '') => {
    try {
      updateState((prevState) => ({
        ...prevState,
        error: '',
        activationCodeSearchPending: true,
      }));

      source.cancel();
      source = CancelToken.source();
      const params = {
        children: false,
        limit: 50,
        order: 'asc',
        org_id: sessionUserRootOrgID,
        page: 1,
        po_number,
        sort: 'po_number',
      };

      const { data } = await assetService.listActivationCodes(
        props.asset?._id,
        params,
        source.token
      );
      updateState((prevState) => ({
        ...prevState,
        error: '',
        activationCodeSearchPending: false,
      }));

      return (
        data?.map?.(
          ({
            _id,
            expiration_date,
            num_assets,
            num_assets_associated,
            org_id,
            po_number,
            type,
            use_for_suborgs,
          }) => ({
            activation_code_id: _id,
            expiration_date,
            label: po_number,
            num_assets,
            num_assets_associated,
            org_id,
            po_number,
            type,
            use_for_suborgs,
            value: _id,
          })
        ) ?? []
      );
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        activationCodeSearchPending: false,
      }));
      return [];
    }
  };

  const fetchModels = async (search = '') => {
    try {
      updateState((prevState) => ({
        ...prevState,
        error: '',
        modelSearchPending: true,
      }));

      source.cancel();
      source = CancelToken.source();

      const { data } = await templateService.fetchPaginatedTemplates(
        1,
        props.asset?.orgID,
        50,
        'name',
        'asc',
        search,
        props.asset?.vendor,
        source.token
      );

      updateState((prevState) => ({
        ...prevState,
        error: '',
        modelSearchPending: false,
      }));
      return data
        .filter(({ _id }) => !PROVISION_MODEL_ID_SET.has(_id))
        .map(({ _id, name }) => ({ label: name, value: _id }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        modelSearchPending: false,
      }));
      return [];
    }
  };

  const fetchModelReminders = useCallback(async () => {
    if (!state.selectedModel?.value) return;
    try {
      updateState((prevState) => ({ ...prevState, remindersLoading: true }));
      const params = {
        gtemplate_id: state.selectedModel.value,
        limit: 500,
        page: 1,
      };

      const { data: activationModelReminders } =
        await reminderService.getPaginatedGtemplateReminders(params);
      updateState((prevState) => ({
        ...prevState,
        activationModelReminders,
        remindersLoading: false,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        remindersLoading: false,
      }));
    }
  }, [reminderService, state.selectedModel?.value]);

  const handleActivateClick = async () => {
    try {
      updateState((prevState) => ({
        ...prevState,
        error: '',
        requestPending: true,
      }));

      source = CancelToken.source();

      const instance_init_values = Array.from(
        state.instanceInitValuesMap
      ).reduce((acc, [reminder_id, initValues]) => {
        const conditionalInitDate = isValid(initValues.initial_date_value)
          ? { initial_date_value: initValues.initial_date_value.toISOString() }
          : {};
        const conditionalInitSignal = Number.isFinite(
          Number.parseFloat(initValues.initial_signal_value)
        )
          ? {
              initial_signal_value: Number.parseFloat(
                initValues.initial_signal_value
              ),
            }
          : {};
        return acc.concat({
          ...conditionalInitDate,
          ...conditionalInitSignal,
          reminder_id,
        });
      }, []);
      const body =
        !assetOrgBillingEnabled || state.associationExists
          ? { instance_init_values }
          : {
              activationCodeID: state.selectedActivationCode?.value,
              instance_init_values,
            };

      const activatedAsset = await templateService.assignTemplate(
        props.asset?._id,
        state.selectedModel?.value,
        body,
        source.token
      );
      props.handleActivateAssetSuccess(activatedAsset);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        requestPending: false,
      }));
    }
  };

  const handleActivationCodeSelected = useCallback((selectedActivationCode) => {
    updateState((prevState) => ({
      ...prevState,
      error: '',
      selectedActivationCode,
    }));
  }, []);

  const handleModelSelected = useCallback((selectedModel) => {
    updateState((prevState) => ({
      ...prevState,
      activationModelReminders: [],
      error: '',
      instanceInitValuesMap: new Map(),
      selectedModel,
    }));
  }, []);

  const handleCalendarReminderInstanceUpdate = useCallback(
      (reminder_id, initial_date_value) => {
        updateState((prevState) => {
          const instanceInitValuesMap = new Map(
            prevState.instanceInitValuesMap
          );
          const targetValues = instanceInitValuesMap.get(reminder_id) || {};
          return {
            ...prevState,
            instanceInitValuesMap: instanceInitValuesMap.set(reminder_id, {
              ...targetValues,
              initial_date_value,
            }),
          };
        });
      },
      []
    ),
    handleSignalReminderInstanceUpdate = useCallback(
      (reminder_id, initial_signal_value) => {
        updateState((prevState) => {
          const instanceInitValuesMap = new Map(
            prevState.instanceInitValuesMap
          );
          const targetValues = instanceInitValuesMap.get(reminder_id) || {};
          return {
            ...prevState,
            instanceInitValuesMap: instanceInitValuesMap.set(reminder_id, {
              ...targetValues,
              initial_signal_value,
            }),
          };
        });
      },
      []
    );

  const isActivationCodeOptionDisabled = useCallback(
    ({ num_assets, num_assets_associated, org_id, type, use_for_suborgs }) => {
      switch (true) {
        case type !== UNLIMITED && num_assets_associated >= num_assets:
          return true;
        case !use_for_suborgs && org_id !== props.asset?.orgID:
          return true;
        default:
          return false;
      }
    },
    [props.asset?.orgID]
  );

  const showActivationCodeSelect = useMemo(
      () => assetOrgBillingEnabled && !state.associationExists,
      [assetOrgBillingEnabled, state.associationExists]
    ),
    showNoPaymentErrorMsg = useMemo(
      () =>
        assetOrgBillingEnabled &&
        !state.associationExists &&
        !hasBillingWritePermission,
      [
        assetOrgBillingEnabled,
        hasBillingWritePermission,
        state.associationExists,
      ]
    ),
    submitIsDisabled = useMemo(() => {
      switch (true) {
        case state.remindersLoading:
        case state.requestPending:
        case !state.selectedModel?.value:
        case assetOrgBillingEnabled &&
          !state.associationExists &&
          !state.selectedActivationCode?.value:
          return true;
        default:
          return false;
      }
    }, [
      assetOrgBillingEnabled,
      state.associationExists,
      state.remindersLoading,
      state.requestPending,
      state.selectedActivationCode?.value,
      state.selectedModel?.value,
    ]);

  const startAtZeroReminders = useMemo(
    () =>
      state.activationModelReminders?.filter?.(
        ({ start_at_zero } = {}) => start_at_zero
      ) ?? [],
    [state.activationModelReminders]
  );

  useEffect(() => {
    fetchModelReminders();
  }, [fetchModelReminders]);

  useEffect(() => {
    if (!assetOrgBillingEnabled) return;

    const checkSource = CancelToken.source();
    (async function checkIfAssociationExists() {
      try {
        const { exists } = await assetService.assetAssociationExists(
          props.asset?._id,
          checkSource.token
        );
        updateState((prevState) => ({
          ...prevState,
          associationExists: exists,
          associationCheckPending: false,
        }));
      } catch {
        updateState((prevState) => ({
          ...prevState,
          associationCheckPending: false,
        }));
      }
    })();
    return checkSource.cancel;
  }, [assetOrgBillingEnabled, assetService, props.asset?._id]);

  return (
    <Modal
      centered
      isOpen={props.isOpen}
      size="lg"
      scrollable
      toggle={props.toggleModal}
    >
      <ModalHeader className="border-0" toggle={props.toggleModal}>
        Activate Asset
      </ModalHeader>
      <ModalBody className="container-fluid">
        {(() => {
          switch (true) {
            case state.associationCheckPending:
              return (
                <div
                  className="row align-items-center justify-content-center"
                  style={{ height: '300px' }}
                >
                  <div className="col-auto">
                    <BrandPrimarySpinningCircle size="3x" />
                  </div>
                </div>
              );
            case showNoPaymentErrorMsg:
              return (
                <div
                  className="row align-items-center justify-content-center"
                  style={{ height: '300px' }}
                >
                  <p className="col-12 col-md-10 text-center text-danger">
                    No form of payment was detected. Please contact your Billing
                    Administrator to provide payment for subscription
                    activation.
                  </p>
                </div>
              );
            default:
              return (
                <>
                  <div className="row align-items-center justify-content-center">
                    {assetOrgBillingEnabled ? (
                      <p className="col-12 col-md-10 modal-supplemental-text text-center text-md-left">
                        Activating an asset will begin a billing subscription.
                        If the asset is associated to a payment method through
                        an Activation Code, the activation will automatically
                        happen. If not, then an Activation Code is required.
                      </p>
                    ) : (
                      <p className="col-12 col-md-10 modal-supplemental-text text-center text-md-left">
                        Choosing an Asset Model will activate a subscription and
                        begin a billing cycle for this asset:{' '}
                        {props.asset?.nickname}.
                      </p>
                    )}
                  </div>
                  <div className="row align-items-center justify-content-center mt-3">
                    <div className="col-12 col-md-10 form-group">
                      <label
                        className="modal-inputlabel-text"
                        htmlFor="assetModel"
                      >
                        Asset Model
                      </label>
                      <Select
                        cacheOptions
                        captureMenuScroll={false}
                        defaultOptions
                        className="flex-grow-1"
                        isClearable
                        isDisabled={state.requestPending}
                        isLoading={state.modelSearchPending}
                        isSearchable
                        loadingMessage={() => 'Searching Asset Models...'}
                        loadOptions={debounce(fetchModels, 1000)}
                        maxMenuHeight={250}
                        menuShouldScrollIntoView
                        name="assetModel"
                        noOptionsMessage={({ inputValue }) =>
                          `No models match: ${inputValue}`
                        }
                        onChange={handleModelSelected}
                        placeholder="Select an Asset Model"
                        styles={BASIC_SELECT_STYLES}
                        value={state.selectedModel}
                      />
                    </div>
                  </div>
                  {showActivationCodeSelect ? (
                    <div className="row align-items-center justify-content-center mt-3 mb-5">
                      <div className="col-12 col-md-10 form-group">
                        <label
                          className="modal-inputlabel-text"
                          htmlFor="activationCode"
                        >
                          Activation Code
                          <FontAwesomeIcon
                            className="ml-3 text-danger"
                            icon={faAsterisk}
                            size="xs"
                          />
                        </label>
                        <p className="mb-0 form-text text-danger three-quarter-text">
                          Activation Code was not found. Please select an
                          Activation Code for payment method.
                        </p>
                        <p className="form-text text-danger three-quarter-text">
                          If no Activation Codes are available please contact
                          your Billing Administrator.
                        </p>
                        <Select
                          cacheOptions
                          captureMenuScroll={false}
                          defaultOptions={false}
                          className="flex-grow-1"
                          formatOptionLabel={formatActivationCodeOptionLabel}
                          isClearable
                          isDisabled={state.requestPending}
                          isLoading={state.activationCodeSearchPending}
                          isMulti={false}
                          isOptionDisabled={isActivationCodeOptionDisabled}
                          isSearchable
                          loadingMessage={() => 'Searching Activation Codes...'}
                          loadOptions={debounce(fetchActivationCodes, 1000)}
                          maxMenuHeight={150}
                          menuShouldScrollIntoView
                          name="activationCode"
                          noOptionsMessage={({ inputValue }) =>
                            inputValue
                              ? `No Activation Codes match: ${inputValue}`
                              : 'Search by PO Number'
                          }
                          onChange={handleActivationCodeSelected}
                          placeholder="Activation Code required to Activate"
                          styles={BASIC_SELECT_STYLES}
                          value={state.selectedActivationCode}
                        />
                      </div>
                    </div>
                  ) : null}
                  {startAtZeroReminders?.length ? (
                    <>
                      <div className="row align-items-center justify-content-center mt-4">
                        <h6 className="col-12 col-md-11">
                          {`Set Maintenance Reminders Initial Value${
                            startAtZeroReminders.length > 1 ? 's' : ''
                          }`}
                        </h6>
                      </div>
                      {startAtZeroReminders.map((r) => {
                        return (
                          <div
                            className="row align-items-center justify-content-center mb-2"
                            key={r._id}
                          >
                            <div className="col-12 col-md-10 row rounded-lg border border-muted py-3">
                              <div className="col-1 d-flex align-items-center justify-content-center">
                                <img
                                  alt="reminders icon"
                                  height={20}
                                  src={reminderIcon}
                                />
                              </div>
                              <div className="col-11">
                                <p className="mb-2">{r.name}</p>
                                {(() => {
                                  switch (r.type) {
                                    case ALL_TYPE:
                                      return (
                                        <>
                                          <div className="mb-2">
                                            <span className="mr-2 three-quarter-text text-muted">
                                              Set next maintenance trigger to
                                            </span>
                                            <input
                                              className="form-control form-control-sm d-inline rounded-lg"
                                              onChange={({
                                                target: { value },
                                              }) =>
                                                handleSignalReminderInstanceUpdate(
                                                  r._id,
                                                  value
                                                )
                                              }
                                              style={{ width: '80px' }}
                                              type="number"
                                              value={
                                                state.instanceInitValuesMap.get(
                                                  r._id
                                                )?.initial_signal_value ?? ''
                                              }
                                            />
                                          </div>
                                          <div className="d-flex align-items-center">
                                            <span className="mr-2 three-quarter-text text-muted">
                                              Set next maintenance trigger to
                                            </span>
                                            <DatePicker
                                              autoComplete="off"
                                              customInput={<InlineDatepicker />}
                                              onChange={(selected) =>
                                                handleCalendarReminderInstanceUpdate(
                                                  r._id,
                                                  selected
                                                )
                                              }
                                              selected={
                                                state.instanceInitValuesMap.get(
                                                  r._id
                                                )?.initial_date_value
                                              }
                                              showYearDropdown
                                              withPortal
                                            />
                                          </div>
                                        </>
                                      );
                                    case CALENDAR_TYPE:
                                      return (
                                        <div className="d-flex align-items-center">
                                          <span className="mr-2 three-quarter-text text-muted">
                                            Set next maintenance trigger to
                                          </span>
                                          <DatePicker
                                            autoComplete="off"
                                            customInput={<InlineDatepicker />}
                                            onChange={(selected) =>
                                              handleCalendarReminderInstanceUpdate(
                                                r._id,
                                                selected
                                              )
                                            }
                                            selected={
                                              state.instanceInitValuesMap.get(
                                                r._id
                                              )?.initial_date_value
                                            }
                                            showYearDropdown
                                            withPortal
                                          />
                                        </div>
                                      );
                                    case SIGNAL_TYPE:
                                      return (
                                        <div>
                                          <span className="mr-2 three-quarter-text text-muted">
                                            Set next maintenance trigger to
                                          </span>
                                          <input
                                            className="form-control form-control-sm d-inline rounded-lg"
                                            onChange={({ target: { value } }) =>
                                              handleSignalReminderInstanceUpdate(
                                                r._id,
                                                value
                                              )
                                            }
                                            style={{ width: '80px' }}
                                            type="number"
                                            value={
                                              state.instanceInitValuesMap.get(
                                                r._id
                                              )?.initial_signal_value ?? ''
                                            }
                                          />
                                        </div>
                                      );
                                    default:
                                      return null;
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : null}
                  <div
                    className="row align-items-end justify-content-end"
                    style={{ height: '100px' }}
                  >
                    <p className="col-12 col-md-10 text-right three-quarter-text text-danger">
                      {state.error}
                    </p>
                  </div>
                </>
              );
          }
        })()}
      </ModalBody>
      <ModalFooter className="border-0">
        <button
          className="btn btn-sm modal-button-submit ml-3 px-md-4 nine-tenth-text"
          disabled={submitIsDisabled}
          onClick={handleActivateClick}
        >
          {state.requestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Activate'
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}

ActivateAssetModal.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    nickname: PropTypes.string,
    orgID: PropTypes.string,
    vendor: PropTypes.string,
  }),
  handleActivateAssetSuccess: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};

function formatActivationCodeOptionLabel(data) {
  const expires =
    data?.expiration_date && isValid(parseISO(data.expiration_date))
      ? format(parseISO(data.expiration_date), 'yyyy-MM-dd')
      : '';
  return (
    <div className="d-flex flex-nowrap align-items-center text-muted">
      <div className="flex-grow-1 d-flex flex-column">
        <p className="mb-0 text-muted fs-eight-tenth text-nowrap">
          {data?.po_number}
        </p>
        <p className="mb-0 fs-three-quarter">{data?.activation_code_id}</p>
      </div>
      <div className="d-flex align-items-center justify-content-end fs-eight-tenth">
        {data?.type === UNLIMITED
          ? `Expires: ${expires}`
          : `${data?.num_assets_associated} / ${data?.num_assets}`}
      </div>
    </div>
  );
}

const InlineDatepicker = forwardRef(function InlineDatePicker(
  { onClick, value },
  ref
) {
  return (
    <div className="input-group input-group-sm rounded-lg">
      <input
        className="form-control border-right-0"
        onClick={onClick}
        ref={ref}
        style={{ width: '120px' }}
        value={value}
      />
      <div className="input-group-append">
        <span className="input-group-text bg-white">
          <FontAwesomeIcon icon={faCalendarDays} />
        </span>
      </div>
    </div>
  );
});

InlineDatepicker.propTypes = {
  onClick: PropTypes.func,
  value: PropTypes.any,
};
